// Payment / cash-flow schedule maths (pure, no Excel): each chapter's cost is spread
// evenly over its working months; an optional advance is paid in month 1 and recouped
// proportionally from the later monthly payments.

/**
 * chapters: [{ number, name, total, start (1-based month), dur (months, >= 1) }]
 * advancePct: 0..100 — share of the grand total paid upfront.
 * Returns { months, work[], payments[], cumulative[], cumulativePct[], perChapter[][], grand }.
 */
export function distributeSchedule(chapters, advancePct = 0) {
  const list = (chapters || []).map((c) => ({
    ...c,
    total: Number(c.total) || 0,
    start: Math.max(1, Math.floor(Number(c.start) || 1)),
    dur: Math.max(1, Math.floor(Number(c.dur) || 1)),
  }));
  const months = list.reduce((m, c) => Math.max(m, c.start + c.dur - 1), 0);
  const grand = list.reduce((s, c) => s + c.total, 0);
  const adv = Math.min(Math.max(Number(advancePct) || 0, 0), 100) / 100;

  const perChapter = list.map((c) =>
    Array.from({ length: months }, (_, i) => {
      const month = i + 1;
      return month >= c.start && month < c.start + c.dur ? c.total / c.dur : 0;
    })
  );
  const work = Array.from({ length: months }, (_, i) => perChapter.reduce((s, row) => s + row[i], 0));
  const payments = work.map((w, i) => w * (1 - adv) + (i === 0 ? adv * grand : 0));

  let running = 0;
  const cumulative = payments.map((p) => (running += p));
  const cumulativePct = cumulative.map((c) => (grand ? c / grand : 0));
  return { months, work, payments, cumulative, cumulativePct, perChapter, grand };
}

/** "2026-11" + n months -> "2027-01" style label (Georgian short month). */
export function monthLabel(startMonth, offset) {
  const [y, m] = String(startMonth || "").split("-").map(Number);
  const base = new Date(Number.isFinite(y) ? y : new Date().getFullYear(), Number.isFinite(m) ? m - 1 : new Date().getMonth(), 1);
  base.setMonth(base.getMonth() + offset);
  return base.toLocaleDateString("ka-GE", { month: "short", year: "2-digit" });
}
